import React from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHandshake,
    faChartLine,
    faShieldAlt,
    faChalkboardTeacher,
    faUserFriends,
    faClock,
} from '@fortawesome/free-solid-svg-icons';
import { motion } from "framer-motion";
import ReadyToPlan from "../components/ReadyToPlan";

export default function SAGApproach() {
    return (
        <div style={{ paddingTop: '150px' }}>


            {/* Intro Section */}
            <section
                className="text-center py-5"
                style={{ backgroundColor: "var(--primary)", color: "var(--white)" }}
            >
                <motion.h1
                    className="fw-bold mb-3"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    A Human-Centered Approach to Financial Planning
                </motion.h1>
                <motion.p
                    className="mx-auto fs-4 w-75"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    At SAG Financials, we know that money isn’t just about math — It’s about life.{" "}
                    <span style={{ whiteSpace: "nowrap" }}>Your life.</span>
                </motion.p>

            </section>

            {/* Understanding Section */}
            <section className="container py-5">
                <motion.p
                    className="fs-5 text-center mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ color: "var(--text)" }}
                >
                    That’s why our approach goes beyond numbers or charts. We understand you first, then craft strategies aligned with your values and goals.
                </motion.p>

            </section>

            {/* Core Philosophy */}
            <section className="py-5" style={{ backgroundColor: "var(--light-bg)" }}>
                <div className="container text-center">

                    <div
                        className="d-flex justify-content-center mb-5"
                    >
                        <h3
                            className="fw-bold text-center py-3 px-4"
                            style={{
                                color: "var(--white)",
                                background: "var(--accent)",  // you can change to any theme color
                                borderRadius: "12px",
                                padding: "10px 25px",
                                boxShadow: "0 6px 20px rgba(0,0,0,0.15)"
                            }}
                        >
                            Our Core Philosophy
                        </h3>
                    </div>
                    <div className="row g-4">
                        {[
                            { icon: faUserFriends, title: "Personal", text: "No two people are alike — so no two plans should be, either." },
                            { icon: faShieldAlt, title: "Transparent", text: "We believe in honesty — no hidden charges or jargon." },
                            { icon: faChartLine, title: "Goal-Driven", text: "We start with your life goals — not financial products." },
                            { icon: faHandshake, title: "Ongoing", text: "Your life changes, and your strategy evolves with it." },
                            { icon: faChalkboardTeacher, title: "Educative", text: "We explain every option clearly and simply." },
                            { icon: faClock, title: "Responsive", text: "We’re always just a call or message away whenever you need us." },

                        ].map(({ icon, title, text }, index) => (
                            <motion.div
                                key={title}
                                className="col-md-6 col-lg-4"
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <div
                                    className="card  shadow h-100 p-4 rounded-3 d-flex flex-column align-items-center justify-content-center text-center"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                        border: "1px solid var(--secondary)"
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-8px)";
                                        e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.1)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.05)";
                                    }}
                                >
                                    {/* Centered Icon */}
                                    <div
                                        className="d-flex align-items-center justify-content-center mb-3"
                                        style={{
                                            width: "70px",
                                            height: "70px",
                                            borderRadius: "50%",
                                            Color: "var(--primary)",
                                            border: "2px solid var(--primary)"
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={icon}
                                            style={{ color: "var(--secondary)", fontSize: "1.8rem" }}
                                        />
                                    </div>

                                    <h5 className="fw-semibold mb-2" style={{ color: "var(--heading)" }}>
                                        {title}
                                    </h5>
                                    <p className="text-muted">{text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>


            {/* 5-Step Process */}
            <section className="py-5" style={{ backgroundColor: "var(--light-bg)" }}>
                <div className="container">
                    <div
                        className="d-flex justify-content-center mb-5"
                    >
                        <h3
                            className="fw-bold text-center py-3 px-4"
                            style={{
                                color: "var(--white)",
                                background: "var(--accent)",  // you can change to any theme color
                                borderRadius: "12px",
                                padding: "10px 25px",
                                boxShadow: "0 6px 20px rgba(0,0,0,0.15)"
                            }}
                        >
                            The SAG 5-Step Process
                        </h3>
                    </div>


                    <div className="row g-4 justify-content-center">
                        {[
                            {
                                step: 1,
                                title: "Discovery: Understanding You",
                                content:
                                    "We start with an in-depth discussion to understand your goals and priorities.",
                            },
                            {
                                step: 2,
                                title: "Analysis & Assessment",
                                content:
                                    "We analyze your income, savings, investments, and risks to identify opportunities.",
                            },
                            {
                                step: 3,
                                title: "Tailored Strategy Development",
                                content:
                                    "We create a personalized plan focusing on investment growth, protection, and tax efficiency.",
                            },
                            {
                                step: 4,
                                title: "Implementation",
                                content:
                                    "We assist with setting up your investments and documentation in a transparent, easy way.",
                            },
                            {
                                step: 5,
                                title: "Monitoring & Adjustments",
                                content:
                                    "We continuously review and adapt your plan as your goals and life evolve.",
                            },
                        ].map(({ step, title, content }, index) => (
                            <motion.div
                                key={step}
                                className="col-12 col-md-6 col-lg-4"
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <div
                                    className="card h-100 shadow-sm border-1 text-center p-4"
                                    style={{
                                        border: `2px solid var(--secondary)`,
                                        borderRadius: "20px",
                                        backgroundColor: "var(--white)",
                                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                    }}
                                    onMouseEnter={(e) =>
                                        (e.currentTarget.style.transform = "translateY(-8px)")
                                    }
                                    onMouseLeave={(e) =>
                                        (e.currentTarget.style.transform = "translateY(0)")
                                    }
                                >
                                    <div
                                        className="step-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "60px",
                                            height: "60px",
                                            borderRadius: "50%",
                                            backgroundColor: "var(--secondary)",

                                            color: 'white',
                                            fontWeight: "700",
                                            fontSize: "1.3rem",
                                        }}
                                    >
                                        {step}
                                    </div>
                                    <h5 className="fw-bold mb-2" style={{ color: "var(--highlight)" }}>
                                        {title}
                                    </h5>
                                    <p className="text-muted mb-0" style={{ color: "var(--text)" }}>
                                        {content}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>


            {/* Call to Action */}
            <ReadyToPlan />


        </div>
    );
}
